import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";

const generateMessages = [
  "Scanning USDA database...",
  "Analyzing nutrient profiles...",
  "Matching foods to your health goals...",
  "Finding delicious options...",
  "Optimizing your grocery list...",
  "Almost there...",
];

const swapMessages = [
  "Finding alternatives...",
  "Comparing nutrient profiles...",
  "Selecting best matches...",
  "Almost ready...",
];

interface LoadingStatusProps {
  type: "generate" | "swap";
}

export const LoadingStatus = ({ type }: LoadingStatusProps) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const messages = type === "generate" ? generateMessages : swapMessages;

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mb-6"
      >
        <Loader2 className="w-8 h-8 text-primary-foreground animate-spin" />
      </motion.div>

      <div className="h-8 relative w-full max-w-xs">
        <AnimatePresence mode="wait">
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="text-center text-muted-foreground font-medium absolute inset-0"
          >
            {messages[messageIndex]}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="flex gap-1.5 mt-6">
        {messages.map((_, i) => (
          <motion.div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              i === messageIndex ? "bg-primary" : "bg-border"
            }`}
            animate={i === messageIndex ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 0.5 }}
          />
        ))}
      </div>
    </div>
  );
};
